import { Store } from "redux";
import { startLoadingState, endLoadingState, loadingTemplate } from "../../components/ticketing/basket-v2/basketLoading";
import { reportError } from "../../components/ticketing/basket-v2/errorReporting";
import { ADD_DONATION, REMOVE_DONATION } from "./store";
import { constants } from "../../components/ticketing/common";
import { postJsonWithResponse, deleteJsonWithResponse, trackEvent, EventEmitter } from "../../utils";
import { events } from "../constants";

export class Donation {
  donation: HTMLElement | null;
  store: Store;
  applyButton: HTMLButtonElement | null;
  donationCharityId: number;
  donationCharityName: string;
  donationSelect: HTMLSelectElement | null;
  appliedDonations: HTMLElement | null;
  removingDonationMessage: string;
  defaultError = { code: 400, message: "Error applying/removing a donation" };

  constructor(selector: string, store: Store) {
    this.donation = document.querySelector(selector);
    this.store = store;
    this.removingDonationMessage = loadingTemplate("Removing donation...");

    if (!this.donation) return;

    this.applyButton = this.donation.querySelector<HTMLButtonElement>("[data-donation-apply]");
    this.donationCharityId = parseInt(this.donation.dataset.donationCharityId ?? "0");
    this.donationCharityName = this.donation.dataset.donationCharityName ?? "Unspecified charity";
    this.donationSelect = this.donation.querySelector<HTMLSelectElement>("[data-donation-select]");
    this.appliedDonations = this.donation.querySelector<HTMLElement>("#appliedDonations");

    this.bindEvents();
  }

  bindEvents() {
    this.applyButton?.addEventListener("click", this.handleApplyDonation);
    this.donation?.addEventListener("click", this.handleRemoveDonation);
  }

  handleApplyDonation = async () => {
    try {
      const donationValue = parseFloat(this.donationSelect?.options[this.donationSelect?.selectedIndex].value ?? "0.00");

      EventEmitter.emit(events.ORDER_UPDATE_START);

      let donationRequest = {
        charityId: this.donationCharityId,
        charityName: this.donationCharityName,
        amount: donationValue,
      };

      const donationResponse = await postJsonWithResponse(constants.urls.basketV2.donation, donationRequest);

      if (!donationResponse.ok) {
        const data = await donationResponse.json();
        reportError(donationResponse.status, data?.message ?? this.defaultError.message);
        return;
      }

      if (this.appliedDonations) {
        this.appliedDonations.innerHTML = await donationResponse.text();
        this.donation?.classList.add("donation-applied");
      }

      this.store.dispatch({
        type: ADD_DONATION,
        payload: {
          amount: donationValue,
        },
      });

      trackEvent({
        event: "checkout",
        category: "Checkout",
        action: "Add Donation",
        label: donationValue.toString(),
      });
    } catch (e) {
      console.log(e);
      reportError(0, "An error occurred adding the donation");
      EventEmitter.emit(events.ORDER_UPDATE_END);
    }
  };

  handleRemoveDonation = async (e: Event) => {
    try {
      if ("donationRemove" in (e.target as HTMLElement).dataset) {
        startLoadingState(this.removingDonationMessage, ".basket-overlay");

        const donationAdded = this.donation?.querySelector<HTMLElement>("[data-donation-added]");

        if (donationAdded) {
          let donationRequest = {
            charityId: this.donationCharityId,
            charityName: this.donationCharityName,
            amount: parseFloat(donationAdded.dataset.donationAmount ?? "0.00"),
          };

          const donationResponse = await deleteJsonWithResponse(constants.urls.basketV2.donation, donationRequest);

          if (!donationResponse.ok) {
            const data = await donationResponse.json();
            reportError(donationResponse.status, data?.message ?? this.defaultError.message);
            return;
          }

          if (this.appliedDonations) {
            this.appliedDonations.innerHTML = await donationResponse.text();
          }

          if (this.donationSelect && this.donationSelect.options) {
            this.donationSelect.value = this.donationSelect.options[0].value;
          }

          this.donation?.classList.remove("donation-applied");

          this.store.dispatch({
            type: REMOVE_DONATION,
          });

          this.donation?.focus();
        }
      }
    } catch (e) {
      console.log(e);
      reportError(0, "An error occurred removing the donation");
    } finally {
      endLoadingState(".basket-overlay");
    }
  };
}
